import { Injectable } from "@angular/core";
import {
  Router,
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from "@angular/router";
import { PaymentsService } from "../services/payments.service";
import { UserService } from "./user.service";

/**
 * Blocks the authenticated app (landing-page, plan, settings, etc.) until the current
 * account has an active Stripe subscription. Runs alongside AuthenticationGuard on the
 * top-level guarded route block - AuthenticationGuard already ensures a valid session
 * exists before this guard's subscription-status check runs.
 */
@Injectable()
export class SubscriptionGuard implements CanActivate, CanActivateChild {
  constructor(
    private router: Router,
    private paymentsService: PaymentsService,
    private userService: UserService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    let status: { status?: string; isActive?: boolean } | null = null;
    try {
      status = await this.paymentsService.checkSubscriptionStatus();
    } catch {
      // The subscription check round-trips to the API, so a stale/invalid token
      // (e.g. an old session the server no longer accepts → 401/500) surfaces here as a
      // thrown error. status stays null and is handled below as "no valid session".
      status = null;
    }

    if (status?.isActive) {
      return true;
    }

    if (status) {
      // A valid session whose subscription has lapsed or been revoked (refund, dispute,
      // cancellation, failed payment, expired fixed-term grant - see FLIP-247/FLIP-245).
      // Keep the session alive and explain the state rather than silently logging out.
      this.router.navigate(["/subscription-expired"], {
        queryParams: { reason: status.status },
      });
      return false;
    }

    // The session couldn't be confirmed at all - matches login.component.authorizeUser,
    // which logs out an unpaid account and returns it to login rather than parking it on
    // /stripe-payments. New/lapsed users reach /stripe-payments through the register →
    // subscribe flow, never via this guard.
    this.userService.logoutOnTokenExp();
    this.router.navigate(["/login"]);
    return false;
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    return this.canActivate(childRoute, state);
  }
}
